import {useCallback, useRef, useState} from 'react';

type UseContainerHeightReturn = {
  containerHeight: number;
  containerRef: (node: HTMLDivElement | null) => void;
};

/**
 * Measure an element's `clientHeight` in real time via a `ResizeObserver`.
 *
 * Returns a callback ref (not a plain `useRef`) so the observer is correctly
 * attached even when the target element mounts after a conditional early-return
 * (e.g. loading guards). The height is floored to whole pixels to avoid
 * jitter re-renders from subpixel differences.
 *
 * Used by `EagGridTable` to detect when the table body area is too short for
 * the inline sidebar and should switch to a fixed drawer (compact mode).
 */
export function useContainerHeight(): UseContainerHeightReturn {
  const [containerHeight, setContainerHeight] = useState(0);
  const lastHeightRef = useRef(0);
  const cleanupRef = useRef<(() => void) | null>(null);

  const containerRef = useCallback((node: HTMLDivElement | null) => {
    cleanupRef.current?.();
    cleanupRef.current = null;

    if (!node) {
      return;
    }

    const measure = () => {
      const height = Math.floor(node.clientHeight);
      if (height !== lastHeightRef.current) {
        lastHeightRef.current = height;
        setContainerHeight(height);
      }
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(node);

    cleanupRef.current = () => {
      observer.disconnect();
    };
  }, []);

  return {containerHeight, containerRef};
}
